import React from 'react';
import { Mail, Linkedin } from 'lucide-react';
import siteContent from '../../content/site/content.json';
import { siteStage } from '../../config/siteStage';

// Footer navigation — grouped per site IA (restructure Sept 2026)
const footerGroups = [
  {
    title: "Residents",
    links: [
      { label: "Residents", href: "/residents" },
      { label: "TRAs & community groups", href: "/resident-groups" },
      { label: "Resident early access", href: "/resident-pilot" },
      { label: "Rights & compliance", href: "/compliance" },
      { label: "Roadmap & feature voting", href: "/features" },
    ],
  },
  {
    title: "Providers",
    links: [
      { label: "Provider pilot", href: "/providers" },
      { label: "Enterprise & integration", href: "/providers/enterprise" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Blog", href: "/blog" },
      { label: "FAQ", href: "/faq" },
      { label: "Launch updates", href: "/updates" },
      { label: "Share your experience", href: "/updates#survey" },
      { label: "About", href: "/about" },
      { label: "Contact", href: "/about#contact" },
    ],
  },
];

const ctaData = { contact: siteContent.contact };

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#1F1E1E] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column (incl. contact details) */}
          <div className="md:col-span-2 lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/logo.png" 
                alt="ResiAX" 
                className="h-12 w-12 object-contain bg-white rounded-xl p-1"
              />
              <div className="flex items-baseline">
                <span className="font-bold text-2xl text-[#B5541B]">RESI</span>
                <span className="font-bold text-2xl text-white">AX</span>
              </div>
            </div>
            <p className="text-gray-400 text-lg leading-relaxed mb-2 max-w-md">
              The home hub for repairs, evidence and communication — clearer for residents,
              easier for providers.
            </p>
            <p className="text-sm text-white/50 italic max-w-md">
              Your home. Your voice. Your rights.
            </p>
            <p className="text-sm text-white/50 italic mb-6 max-w-md">
              Your homes. Your teams. Your standards.
            </p>
            <div className="flex gap-4 mb-6">
              <a
                href="https://www.linkedin.com/company/teqie"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#B5541B] flex items-center justify-center transition-colors duration-300"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="mailto:Shax@teqie.online"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#B5541B] flex items-center justify-center transition-colors duration-300"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
            <div className="space-y-1.5 text-sm text-gray-400" data-testid="footer-contact-details">
              <a 
                href="https://www.teqie.online" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-medium text-white hover:text-[#B5541B] transition-colors block"
              >
                {ctaData.contact.company}
              </a>
              <a
                href="https://www.linkedin.com/in/shakirahakinwale"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#B5541B] transition-colors block"
              >
                {ctaData.contact.person}
              </a>
              <a
                href="mailto:Shax@teqie.online"
                className="flex items-center gap-2 hover:text-[#B5541B] transition-colors duration-200"
              >
                <Mail className="w-4 h-4" />
                Shax@teqie.online
              </a>
            </div>
          </div>

          {/* Grouped navigation — spread across columns on desktop */}
          {footerGroups.map((group) => (
            <div key={group.title}>
              <h4 className="font-semibold text-base mb-4">{group.title}</h4>
              <ul className="space-y-2.5">
                {group.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      data-testid={`footer-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-link`}
                      className="text-sm text-gray-400 hover:text-[#B5541B] transition-colors duration-200"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
              {group.title === 'Providers' && (
                <div className="mt-8">
                  <h4 className="font-semibold text-base mb-4">Account</h4>
                  <a
                    href="https://app.resiax.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-login-link"
                    className="text-sm text-[#B5541B] hover:text-[#D4722F] font-medium transition-colors duration-200"
                  >
                    Log In →
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Legal disclaimer */}
        <div className="mt-14 pt-8 border-t border-white/10">
          <p className="text-xs text-white/50 leading-relaxed max-w-3xl">
            ResiAX helps you organise information and understand possible next steps. It is not a substitute for independent legal advice.
          </p>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-6 border-t border-white/5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} {ctaData.contact.company}. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-gray-500">
              <a href="/privacy" data-testid="footer-privacy-link" className="hover:text-[#B5541B] transition-colors">Privacy Policy</a>
              {siteStage.publicPricingEnabled && (
                <a href="/fees" data-testid="footer-fees-link" className="hover:text-[#B5541B] transition-colors">Fees</a>
              )}
              <span className="text-gray-600 cursor-default" title="Coming soon">Terms of Service (coming soon)</span>
              <a href="/status" data-testid="footer-status-link" className="hover:text-[#B5541B] transition-colors">Access check</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
